<template>
    <MainContent :title="familia?.nombre || 'Detalle de familia'">
        <div v-if="!familia && !loading" class="tw:text-center tw:py-12 tw:text-gray-500">Familia no encontrada</div>

        <div v-if="familia || loading" class="tw:space-y-6">
            <!-- Family header -->
            <Card v-if="familia">
                <div class="tw:flex tw:items-center tw:justify-between">
                    <div>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <h3 class="tw:text-base tw:font-semibold tw:text-gray-900">{{ familia.nombre || 'Sin nombre' }}</h3>
                            <button
                                class="tw:inline-flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-lg tw:text-blue-600 hover:tw:bg-blue-50 tw:transition-colors"
                                title="Editar nombre"
                                @click="openEditNombreModal"
                            >
                                <i class="material-icons tw:text-lg">edit</i>
                            </button>
                        </div>
                        <p class="tw:text-sm tw:text-gray-500">
                            Titular: {{ familia.titular?.name }} {{ familia.titular?.surnames }}
                            <span class="tw:text-gray-400 tw:mx-1">·</span>
                            Nº de abonado {{ familia.titular?.n_abonado }}
                        </p>
                    </div>
                    <div class="tw:flex tw:flex-col-reverse tw:items-center tw:gap-3">
                        <Button
                            v-if="familia.estado !== 'aprobado'"
                            variant="primary"
                            @click="handleApprove(familia)"
                        >
                            Aprobar
                        </Button>
                        <Button
                            v-if="familia.estado !== 'rechazado'"
                            variant="danger"
                            @click="openMotivoDialog(familia)"
                        >
                            Rechazar
                        </Button>
                        <span
                            class="tw:inline-flex tw:items-center tw:px-2.5 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium"
                            :class="estadoBadgeClass(familia.estado)"
                        >
                            {{ estadoLabel(familia.estado) }}
                        </span>
                    </div>
                </div>
            </Card>

            <div
                v-if="familia && (familia.estado === 'aprobado' || familia.estado === 'rechazado')"
                class="tw:rounded-lg tw:border tw:p-3 tw:text-sm"
                :class="{
                    'tw:border-green-200 tw:bg-green-50 tw:text-green-800': familia.estado === 'aprobado',
                    'tw:border-red-200 tw:bg-red-50 tw:text-red-800': familia.estado === 'rechazado',
                }"
            >
                <i
                    class="material-icons tw:text-base tw:align-middle tw:mr-1"
                >{{ familia.estado === 'aprobado' ? 'check_circle' : 'cancel' }}</i>
                <template v-if="familia.estado === 'rechazado' && familia.motivo_rechazo">
                    <strong>Motivo del rechazo:</strong> {{ familia.motivo_rechazo }}
                    <br/>
                </template>
                <span class="tw:text-xs tw:opacity-75">
                    Revisado por {{ familia.revisado_por?.name || '—' }} el {{ familia.revisado_en || '—' }}
                </span>
            </div>

            <!-- Toolbar -->
            <div class="tw:flex tw:justify-end">
                <Button icon="fas fa-plus" @click="openAddModal">Añadir miembro</Button>
            </div>

            <!-- Members table -->
            <div class="tw:bg-white tw:rounded-xl">
                <BaseTable
                    :columns="columns"
                    :data="members.data || []"
                    :default-col-width="defaultColWidth"
                    :loading="loading"
                    empty-text="No hay miembros en esta familia"
                    custom-class="tw:min-h-[200px]"
                >
                    <template #cell-name="{ row }">
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <span class="tw:font-medium tw:text-gray-900">{{ row.name }} {{ row.surnames }}</span>
                            <span v-if="row.id === familia.titular?.id" class="tw:inline-flex tw:items-center tw:px-1.5 tw:py-0.5 tw:rounded tw:text-xs tw:font-medium tw:bg-blue-100 tw:text-blue-700">Titular</span>
                        </div>
                    </template>
                    <template #cell-n_abonado="{ row }">
                        <span class="tw:text-sm tw:text-gray-600">{{ row.n_abonado }}</span>
                    </template>
                    <template #cell-dni="{ row }">
                        <span class="tw:text-sm tw:text-gray-600">{{ row.dni }}</span>
                    </template>
                    <template #cell-tipo_abono="{ row }">
                        <span class="tw:text-sm tw:text-gray-600">{{ row.tipo_abono || '-' }}</span>
                    </template>
                    <template #cell-renovacion="{ row }">
                        <span
                            class="tw:inline-flex tw:items-center tw:px-2.5 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium"
                            :class="renovacionBadgeClass(row.estado_renovacion)"
                        >
                            {{ renovacionLabel(row.estado_renovacion) }}
                        </span>
                    </template>
                    <template #cell-acciones="{ row }">
                        <OptionsDropdownComponent :options="getRowOptions(row)" />
                    </template>
                </BaseTable>
            </div>

            <!-- Add member modal -->
            <ModalComponent name="add-miembro-familia-show" title="Añadir miembro" @close="closeAddModal" @closed="closeAddModal">
                <div class="tw:flex tw:flex-col tw:gap-3">
                    <div class="tw:flex tw:items-start tw:gap-3">
                        <div class="tw:flex-1">
                            <BaseInputText label="Nº abonado" name="numero_abonado" :value="addForm.numero_abonado" placeholder="Nº abonado" @input="addForm.numero_abonado = $event" />
                        </div>
                        <div class="tw:flex-1">
                            <BaseInputText label="DNI" name="dni" :value="addForm.dni" placeholder="DNI" @input="addForm.dni = $event" />
                        </div>
                    </div>
                    <span v-if="addError" class="tw:text-xs tw:text-red-500 tw:leading-tight" v-html="addError.replace(/\n/g, '<br>')"></span>
                </div>
                <template #footer>
                    <div class="tw:flex tw:justify-end tw:gap-2">
                        <Button variant="secondary" @click="closeAddModal">Cancelar</Button>
                        <Button variant="primary" :disabled="addLoading || !addForm.numero_abonado.trim() || !addForm.dni.trim()" @click="handleAdd">
                            {{ addLoading ? 'Añadiendo...' : 'Añadir' }}
                        </Button>
                    </div>
                </template>
            </ModalComponent>

            <!-- Delete member modal -->
            <BorrarModalComponent
                v-if="memberToRemove"
                :name="'borrar-miembro-show-' + memberToRemove.id"
                title="Eliminar miembro"
                @confirm="confirmRemove"
                @cancel="cancelRemove"
                @close="cancelRemove"
            >
                ¿Está seguro de que quiere eliminar a {{ memberToRemove.name }} {{ memberToRemove.surnames }} de la familia?
            </BorrarModalComponent>

            <!-- Approve modal -->
            <ModalComponent
                v-if="approveData"
                :name="'aprobar-familia-show-' + approveData.id"
                title="Aprobar familia"
                @close="approveData = null"
            >
                <div class="tw:text-sm tw:text-gray-600 tw:space-y-2">
                    <p>¿Está seguro de que quiere <strong>aprobar</strong> la familia <strong>{{ approveData.nombre || 'Sin nombre' }}</strong>?</p>
                    <p>Al aprobarla, los miembros podrán acceder a los <strong>beneficios de precios para familias</strong>.</p>
                </div>
                <template #footer>
                    <div class="tw:flex tw:justify-end tw:gap-2">
                        <Button variant="secondary" @click="approveData = null" :disabled="approveLoading">Cancelar</Button>
                        <Button variant="primary" :loading="approveLoading" @click="confirmApprove">Aprobar familia</Button>
                    </div>
                </template>
            </ModalComponent>

            <!-- Edit nombre modal -->
            <RenameFamiliaModal
                v-if="editNombreOpen"
                :name="editNombreModalName"
                :nombre="editNombreValue"
                :original="familia?.nombre"
                :loading="editNombreLoading"
                :error="editNombreError"
                @update:nombre="editNombreValue = $event"
                @submit="handleEditNombre"
                @close="closeEditNombreModal"
            />

            <!-- Motivo rechazo -->
            <MotivoDialog
                v-if="motivoDialog.open"
                :name="'motivo-rechazo-show-' + motivoDialog.familia?.id"
                :familia="motivoDialog.familia"
                :loading="rejectLoading"
                :error="motivoDialog.error"
                @confirm="handleRejectConfirm"
                @cancel="closeMotivoDialog"
            />
        </div>
    </MainContent>
</template>

<script setup>
    import { ref, computed, onMounted, getCurrentInstance, nextTick, watch, reactive } from 'vue'
    import { useRoute } from '@/composables/common/router/useRouter.js'
    import { useFamilias } from '@/composables/admin/abonados/useFamilias'
    import { useToast } from '@/composables/common/useToast'
    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import ModalComponent from '@/modules/admin/ui/ModalComponent.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import MotivoDialog from '@/modules/admin/abonados/familias/components/MotivoDialog.vue'
    import RenameFamiliaModal from '@/modules/admin/abonados/familias/modals/RenameFamiliaModal.vue'
    import { estadoBadgeClass, estadoLabel, renovacionBadgeClass, renovacionLabel } from '@/modules/admin/abonados/familias/utils.js'

    defineProps({
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    })

    const route = useRoute()
    const instance = getCurrentInstance()
    const toast = useToast()
    const { familia, members, fetchFamilia, addMember, removeMember, approveFamily, rejectFamily, updateNombre } = useFamilias()

    const familiaId = computed(() => route.params.familia_id)
    const loading = ref(false)
    const memberToRemove = ref(null)
    const addForm = reactive({ numero_abonado: '', dni: '' })
    const addLoading = ref(false)
    const addError = ref(null)

    // Edit nombre state
    const editNombreOpen = ref(false)
    const editNombreValue = ref('')
    const editNombreLoading = ref(false)
    const editNombreError = ref(null)
    const editNombreModalName = `editar-nombre-familia-show-${instance.uid}`

    function openEditNombreModal() {
        editNombreValue.value = familia.value?.nombre || ''
        editNombreError.value = null
        editNombreOpen.value = true
        nextTick(() => instance.proxy.$modal.show(editNombreModalName))
    }

    function closeEditNombreModal() {
        instance.proxy.$modal.hide(editNombreModalName)
        editNombreOpen.value = false
        editNombreValue.value = ''
        editNombreLoading.value = false
        editNombreError.value = null
    }

    async function handleEditNombre(nombre) {
        const trimmed = (nombre || '').trim()
        if (!trimmed || trimmed === (familia.value?.nombre || '').trim()) return
        editNombreLoading.value = true
        editNombreError.value = null
        try {
            await updateNombre(familiaId.value, trimmed)
            closeEditNombreModal()
            // Refrescar detalle
            loading.value = true
            try {
                await fetchFamilia(familiaId.value)
            } catch (e) {
                // Silencioso — el toast de updateNombre ya confirmó el éxito
            } finally {
                loading.value = false
            }
        } catch (err) {
            editNombreError.value = err?.response?.data?.error || err?.response?.data?.message || 'Error al actualizar el nombre'
        } finally {
            editNombreLoading.value = false
        }
    }

    // Approve / Reject state
    const approveData = ref(null)
    const approveLoading = ref(false)
    const rejectLoading = ref(false)
    const motivoDialog = ref({
        open: false,
        familia: null,
        error: null,
    })

    const columns = [
        { key: 'name', label: 'Nombre', class: 'tw:w-56' },
        { key: 'n_abonado', label: 'Nº Abonado', centered: true, class: 'tw:w-28' },
        { key: 'dni', label: 'DNI' },
        { key: 'tipo_abono', label: 'Tipo Abono', class: 'tw:w-40' },
        { key: 'renovacion', label: 'Estado', centered: true, class: 'tw:w-28' },
        { key: 'acciones', label: '' , centered: true, class: 'tw:w-32'},
    ]

    function openAddModal() {
        addForm.numero_abonado = ''
        addForm.dni = ''
        addError.value = null
        instance.proxy.$modal.show('add-miembro-familia-show')
    }

    function getRowOptions(row) {
        return [{ type: 'action', action: () => openRemoveModal(row), label: 'Eliminar', icon: 'trash' }]
    }

    function openRemoveModal(row) {
        memberToRemove.value = row
        nextTick(() => instance.proxy.$modal.show('borrar-miembro-show-' + row.id))
    }


    async function handleAdd() {
        if (!addForm.numero_abonado.trim() || !addForm.dni.trim()) return
        addLoading.value = true
        addError.value = null
        try {
            await addMember(familiaId.value, addForm.numero_abonado.trim(), addForm.dni.trim())
            closeAddModal()
        } catch (err) {
            addError.value = err?.response?.data?.error || err?.response?.data?.message || 'Error al añadir miembro'
        } finally {
            addLoading.value = false
        }
    }

    function closeAddModal() {
        instance.proxy.$modal.hide('add-miembro-familia-show')
        addForm.numero_abonado = ''
        addForm.dni = ''
        addError.value = null
    }

    async function confirmRemove() {
        const id = memberToRemove.value?.id
        instance.proxy.$modal.hide('borrar-miembro-show-' + id)
        memberToRemove.value = null
        if (id) await removeMember(familiaId.value, id)
    }

    function cancelRemove() {
        if (memberToRemove.value) instance.proxy.$modal.hide('borrar-miembro-show-' + memberToRemove.value.id)
        memberToRemove.value = null
    }

    // ————— Approve / Reject —————

    watch(approveData, async val => {
        if (val) { await nextTick(); instance.proxy.$modal.show('aprobar-familia-show-' + val.id) }
    })

    watch(() => motivoDialog.value.open, async val => {
        if (val && motivoDialog.value.familia) {
            await nextTick()
            instance.proxy.$modal.show('motivo-rechazo-show-' + motivoDialog.value.familia.id)
        }
    })

    async function handleApprove(familia) {
        approveData.value = familia
    }

    async function confirmApprove() {
        const row = approveData.value
        approveLoading.value = true
        try {
            await approveFamily(row.id)
            instance.proxy.$modal.hide('aprobar-familia-show-' + row.id)
            approveData.value = null
            // Refrescar detalle (fallo silencioso para no bloquear el cierre del modal)
            loading.value = true
            try {
                await fetchFamilia(familiaId.value)
            } catch (e) {
                // Silencioso — el toast de approveFamily ya confirmó el éxito
            } finally {
                loading.value = false
            }
        } catch (err) {
            // Toast ya lo maneja approveFamily
        } finally {
            approveLoading.value = false
        }
    }

    function openMotivoDialog(familia) {
        motivoDialog.value = { open: true, familia, error: null }
    }

    function closeMotivoDialog() {
        instance.proxy.$modal.hide('motivo-rechazo-show-' + motivoDialog.value.familia?.id)
        motivoDialog.value = { open: false, familia: null, error: null }
    }

    async function handleRejectConfirm(motivo) {
        const familia = motivoDialog.value.familia
        if (!familia) return
        rejectLoading.value = true
        try {
            await rejectFamily(familia.id, motivo)
            instance.proxy.$modal.hide('motivo-rechazo-show-' + familia.id)
            motivoDialog.value = { open: false, familia: null, error: null }
            // Refrescar detalle (fallo silencioso para no bloquear el cierre del modal)
            loading.value = true
            try {
                await fetchFamilia(familiaId.value)
            } catch (e) {
                // Silencioso — el toast de rejectFamily ya confirmó el éxito
            } finally {
                loading.value = false
            }
        } catch (err) {
            motivoDialog.value.error = err?.response?.data?.error || err?.response?.data?.message || 'Error al rechazar la familia'
        } finally {
            rejectLoading.value = false
        }
    }

    // ————— End Approve / Reject —————

    onMounted(async() => {
        loading.value = true
        try {
            await fetchFamilia(familiaId.value)
        } finally {
            loading.value = false
        }
    })
</script>
